import { useState, useEffect, useRef } from "react";
import { Modal, Button } from "react-bootstrap";

function formatTime(totalSeconds) {
  const m = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, "0");
  const s = (totalSeconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

const EVENT_PHASE = {
  TIMER_STARTED: "connecting_user",
  CLIENT_CALL_WAITING_FOR_LOCAL_PICKUP: "connecting_user",
  CLIENT_CALL_IN_PROGRESS: "ringing_client",
  CLIENT_CALL_COMPLETED: "complete",
  CLIENT_CALL_FAILED: "failed",
  CLIENT_CALL_BUSY: "busy",
  TRANSCRIPTION_IN_PROGRESS: "transcribing",
};

const RINGING_CLIENT_DELAY = 3500;

const CallStatusModal = ({ show, onClose, wsEvent }) => {
  const [phase, setPhase] = useState("idle");
  const [wsMessage, setWsMessage] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [finalDuration, setFinalDuration] = useState(null);

  const timerRef = useRef(null);
  const ringingTimer = useRef(null);
  const activeCallId = useRef(null);

  const resetState = () => {
    clearInterval(timerRef.current);
    clearTimeout(ringingTimer.current);
    timerRef.current = null;
    ringingTimer.current = null;
    setPhase("idle");
    setWsMessage("");
    setElapsed(0);
    setIsTranscribing(false);
    setFinalDuration(null);
  };

  useEffect(() => {
    if (!show) {
      resetState();
      activeCallId.current = null;
    }
  }, [show]);

  useEffect(() => {
    if (!wsEvent) return;

    const { event, message, duration_seconds, call_id } = wsEvent;
    const nextPhase = EVENT_PHASE[event];
    if (!nextPhase) return;

    if (event === "TIMER_STARTED") {
      resetState();
      activeCallId.current = call_id;
      setPhase("connecting_user");
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
      return;
    }

    if (call_id && activeCallId.current && call_id !== activeCallId.current) return;

    const silentEvents = ["CLIENT_CALL_WAITING_FOR_LOCAL_PICKUP"];
    if (!silentEvents.includes(event)) {
      setWsMessage(message || "");
    }

    if (nextPhase === "transcribing") {
      setIsTranscribing(true);
      return;
    }

    if (nextPhase === "ringing_client") {
      setPhase("ringing_client");
      clearTimeout(ringingTimer.current);
      ringingTimer.current = setTimeout(() => {
        setPhase("in_progress");
      }, RINGING_CLIENT_DELAY);
      return;
    }

    setPhase(nextPhase);

    if (nextPhase === "complete" || nextPhase === "failed" || nextPhase === "busy") {
      clearInterval(timerRef.current);
      clearTimeout(ringingTimer.current);
      timerRef.current = null;
      if (duration_seconds) setFinalDuration(duration_seconds);
    }
  }, [wsEvent]);

  useEffect(
    () => () => {
      clearInterval(timerRef.current);
      clearTimeout(ringingTimer.current);
    },
    []
  );

  const isComplete = phase === "complete";
  const isFailed = phase === "failed";
  const isBusy = phase === "busy";
  const isInProgress = phase === "in_progress";
  const isRingingClient = phase === "ringing_client";
  const isDone = isComplete || isFailed || isBusy;

  const StepRow = ({ label, state }) => {
    const bg = {
      active: isInProgress ? "#fff8f8" : "#f0f6ff",
      done: "#f5faf5",
      pending: "#fafafa",
      failed: "#fff5f5",
      busy: "#fffbf0",
    }[state];

    const border = {
      active: isInProgress ? "#f5c2c2" : "#b8d4f5",
      done: "#c3e6cb",
      pending: "#e8e8e8",
      failed: "#f5c6cb",
      busy: "#ffeeba",
    }[state];

    const textColor = {
      active: isInProgress ? "#c0392b" : "#1a4f8a",
      done: "#27ae60",
      pending: "#999",
      failed: "#c0392b",
      busy: "#856404",
    }[state];

    const icon = {
      active: <span className="csm-spinner" />,
      done: <span style={{ color: "#28a745", fontWeight: 700, fontSize: 14 }}>✓</span>,
      pending: <span style={{ color: "#ccc", fontSize: 12 }}>○</span>,
      failed: <span style={{ color: "#c0392b", fontWeight: 700, fontSize: 14 }}>✕</span>,
      busy: <span style={{ color: "#856404", fontWeight: 700, fontSize: 14 }}>–</span>,
    }[state];

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 12px",
          borderRadius: 6,
          background: bg,
          border: `1px solid ${border}`,
          transition: "all 0.35s ease",
        }}
      >
        <div style={{ width: 20, display: "flex", justifyContent: "center", flexShrink: 0 }}>
          {icon}
        </div>
        <span
          style={{
            fontSize: 13,
            fontWeight: state === "active" ? 600 : 400,
            color: textColor,
            flex: 1,
          }}
        >
          {label}
        </span>
        {state === "active" && isInProgress && <span className="csm-record-dot" />}
      </div>
    );
  };

  const step1State = (() => {
    if (phase === "idle" || phase === "connecting_user") return "active";
    return "done";
  })();

  const step2State = (() => {
    if (phase === "idle" || phase === "connecting_user") return "pending";
    if (isRingingClient) return "active";
    if (isFailed) return "failed";
    if (isBusy) return "busy";
    return "done";
  })();

  const step3State = (() => {
    if (phase === "idle" || phase === "connecting_user" || isRingingClient) return "pending";
    if (isInProgress) return "active";
    if (isFailed) return "failed";
    if (isBusy) return "busy";
    return "done";
  })();

  const step4State = (() => {
    if (isComplete || isTranscribing) return "done";
    if (isFailed) return "failed";
    if (isBusy) return "busy";
    return "pending";
  })();

  const displayDuration = finalDuration ?? elapsed;

  return (
    <Modal show={show} onHide={() => {}} centered backdrop="static" keyboard={false} size="sm">
      <Modal.Header
        style={{ borderBottom: "0px" }}
        className="modal-header text-center p-0 bg-primary popup-heading-color justify-content-center"
      >
        <h5 className="modal-title d-flex align-items-center justify-content-center font-size-20 height-25 font-weight-semibold text-uppercase popup-heading-color font-weight-500">
          Call Status Monitor
        </h5>
      </Modal.Header>

      <Modal.Body style={{ padding: "12px", minWidth: 300, minHeight: 300 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
          <StepRow label="Connecting your line…" state={step1State} />
          <StepRow label="Ringing client…" state={step2State} />
          <StepRow label="Call in progress — Recording" state={step3State} />
          <StepRow
            label={
              isFailed ? wsMessage || "Call failed." : isBusy ? "Call complete" : "Call complete"
            }
            state={step4State}
          />
        </div>

        {/* Live timer */}
        {!isDone && (
          <div
            style={{
              textAlign: "center",
              fontFamily: "'Courier New', monospace",
              fontSize: 28,
              fontWeight: 700,
              letterSpacing: 3,
              color: isInProgress ? "#c0392b" : "#1a4f8a",
              marginBottom: 12,
              padding: "8px",
              background: isInProgress ? "#fff8f8" : "#f0f6ff",
              borderRadius: 6,
              border: `1px solid ${isInProgress ? "#f5c2c2" : "#b8d4f5"}`,
            }}
          >
            {formatTime(elapsed)}
          </div>
        )}

        {/* Completion card */}
        {isComplete && (
          <div
            style={{
              background: "#f5faf5",
              border: "1px solid #c3e6cb",
              borderRadius: 6,
              padding: "12px 14px",
              marginBottom: 8,
            }}
          >
            <p style={{ margin: "0 0 4px", fontWeight: 600, fontSize: 13, color: "#1d6f3b" }}>
              Call Duration: {formatTime(displayDuration)}
            </p>
            {isTranscribing ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                <span
                  className="csm-spinner"
                  style={{ borderColor: "#b8d4f5", borderTopColor: "#1a4f8a" }}
                />
                <span style={{ fontSize: 13, fontWeight: 600, color: "#1a4f8a" }}>
                  Transcription in progress…
                </span>
              </div>
            ) : (
              <>
                <p style={{ margin: "0 0 2px", fontWeight: 600, fontSize: 13, color: "#155724" }}>
                  Transcription Processing
                </p>
                <p style={{ margin: 0, fontSize: 12, color: "#555" }}>
                  Transcript will be available shortly.
                </p>
              </>
            )}
          </div>
        )}

        {/* Busy card */}
        {isBusy && (
          <div
            style={{
              background: "#fffbf0",
              border: "1px solid #ffeeba",
              borderRadius: 6,
              padding: "12px 14px",
              marginBottom: 8,
            }}
          >
            <p style={{ margin: "0 0 4px", fontWeight: 600, fontSize: 13, color: "#856404" }}>
              Client Line Busy
            </p>
            <p style={{ margin: 0, fontSize: 12, color: "#555" }}>
              {wsMessage || "The client's line is currently busy. Please try again later."}
            </p>
          </div>
        )}

        {/* Failed card */}
        {isFailed && (
          <div
            style={{
              background: "#fff5f5",
              border: "1px solid #f5c6cb",
              borderRadius: 6,
              padding: "12px 14px",
              marginBottom: 8,
            }}
          >
            <p style={{ margin: 0, fontWeight: 600, fontSize: 13, color: "#c0392b" }}>
              {wsMessage || "The call could not be connected. Please try again."}
            </p>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer
        className="p-0 mt-0 padding-outside-btn-new-provider-2 d-flex align-items-center justify-content-center"
        style={{ borderTop: "none" }}
      >
        <Button
          variant={isDone ? "primary" : "secondary"}
          className="button-padding-footer-new-provider p-0 d-flex align-items-center justify-content-center"
          onClick={onClose}
          style={{ fontSize: 14, fontWeight: 600, minWidth: 220 }}
        >
          Close Call Status Monitor
        </Button>
      </Modal.Footer>

      <style>{`
        .csm-spinner {
          display: inline-block;
          width: 14px; height: 14px;
          border: 2px solid #b8d4f5;
          border-top-color: #1a4f8a;
          border-radius: 50%;
          animation: csm-spin 0.75s linear infinite;
          flex-shrink: 0;
        }
        @keyframes csm-spin { to { transform: rotate(360deg); } }
        .csm-record-dot {
          display: inline-block;
          width: 10px; height: 10px;
          background: #e74c3c;
          border-radius: 50%;
          animation: csm-pulse 1s ease-in-out infinite;
          flex-shrink: 0;
        }
        @keyframes csm-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.4; transform: scale(0.75); }
        }
      `}</style>
    </Modal>
  );
};

export default CallStatusModal;
